import React, { useState, useEffect, useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  Button,
  Tooltip,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import api, { API_BASE_URL } from '../../src/utils/api';

const MembersGridPage = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm')); // <600px
  const isSmDown = useMediaQuery(theme.breakpoints.down('md')); // <900px

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  // backend paging (1-based)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // search controlled + debounced
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // View toggle: "table" | "cards"
  const [viewMode, setViewMode] = useState('table');

  // ✅ Cards: render fewer items + "Load more"
  const [cardVisibleCount, setCardVisibleCount] = useState(12);

  // ✅ Dynamic limit (cards should fetch fewer records than table)
  const limit = useMemo(() => {
    if (viewMode === 'cards') return isXs ? 24 : 48;
    return 100;
  }, [viewMode, isXs]);

  // Default to cards on XS, table otherwise
  useEffect(() => {
    if (isXs) setViewMode('cards');
    else setViewMode('table');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isXs]);

  // debounce search
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  // when searching or limit changes, go back to page 1
  useEffect(() => setCurrentPage(1), [debouncedSearch, limit]);

  // reset cards visible count when paging/search/view
  useEffect(() => {
    setCardVisibleCount(isXs ? 12 : 24);
  }, [isXs, viewMode, currentPage, debouncedSearch]);

  useEffect(() => {
    fetchMembers(currentPage, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, limit]);

  const fetchMembers = async (page, search, { force = false } = {}) => {
    setLoading(true);

    // ✅ include limit in cache key so cards/table don't reuse wrong cache
    const cacheKey = `members-page-${page}-limit-${limit}-search-${search}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (!force && cachedData) {
      const { members, totalRecords } = JSON.parse(cachedData);
      setMembers(members || []);
      setTotalRecords(totalRecords || 0);
      setLoading(false);
      return;
    }

    if (force) sessionStorage.removeItem(cacheKey);

    try {
      const response = await api.get('/membersfilter', {
        params: { page, limit, search },
        headers: force ? { 'Cache-Control': 'no-cache' } : undefined,
      });

      const data = response.data || {};
      setMembers(data.members || []);
      setTotalRecords(data.totalRecords || 0);

      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
          members: data.members || [],
          totalRecords: data.totalRecords || 0,
        })
      );
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrent = () => {
    fetchMembers(currentPage, debouncedSearch, { force: true });
  };

  const clearAllMembersCache = () => {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('members-page-'))
      .forEach((k) => sessionStorage.removeItem(k));

    fetchMembers(currentPage, debouncedSearch, { force: true });
  };

  // EXTRA: client-side filter to ensure FullAddress is included even if backend doesn't filter by it
  const displayedRows = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    if (!q) return members;

    return members.filter((m) => {
      const fields = [
        m.CompanyCode,
        m.ClubMemberCode,
        m.LastName,
        m.FirstName,
        m.ClubMembershipTypeCode,
        m.ClubStatusRuleCode,
        m.EmailAddress,
        m.FullAddress,
      ];
      return fields.some((val) => String(val || '').toLowerCase().includes(q));
    });
  }, [members, debouncedSearch]);

  // ✅ Cards only show a slice (performance)
  const cardsRows = useMemo(() => {
    if (viewMode !== 'cards') return displayedRows;
    return displayedRows.slice(0, cardVisibleCount);
  }, [displayedRows, viewMode, cardVisibleCount]);

  /**
   * ✅ Responsive Column Hiding
   */
  const columnVisibilityModel = useMemo(() => {
    if (isXs) {
      return {
        CompanyCode: false,
        PhotoGraphicFileName: false,
        ClubMembershipTypeCode: false,
        ClubStatusRuleCode: false,
        EmailAddress: true,
        FullAddress: true,
        LastName: true,
        FirstName: true,
        ClubMemberCode: true,
      };
    }
    if (isSmDown) {
      return {
        CompanyCode: false,
        PhotoGraphicFileName: true,
        ClubMembershipTypeCode: true,
        ClubStatusRuleCode: false,
        EmailAddress: true,
        FullAddress: true,
        LastName: true,
        FirstName: true,
        ClubMemberCode: true,
      };
    }
    return {
      CompanyCode: true,
      PhotoGraphicFileName: true,
      ClubMembershipTypeCode: true,
      ClubStatusRuleCode: true,
      EmailAddress: true,
      FullAddress: true,
      LastName: true,
      FirstName: true,
      ClubMemberCode: true,
    };
  }, [isXs, isSmDown]);

  const columns = useMemo(() => {
    return [
      { field: 'CompanyCode', headerName: 'Company Code', flex: 1, minWidth: 140 },
      {
        field: 'ClubMemberCode',
        headerName: 'Club Member Code',
        flex: 1,
        minWidth: 170,
        renderCell: (params) => (
          <Button
            component={RouterLink}
            to={`/home/clubmembers/${params.row.ClubMemberCode}`}
            variant="text"
            size="small"
            sx={{ color: '#1976d2', fontWeight: 700, textTransform: 'none', px: 0 }}
          >
            {params.value?.trim()}
          </Button>
        ),
      },
      {
        field: 'PhotoGraphicFileName',
        headerName: 'Photo',
        flex: 0.7,
        minWidth: 110,
        sortable: false,
        renderCell: (params) => (
          <img
            loading="lazy"
            src={`${API_BASE_URL}/images/${encodeURIComponent(
              params.row?.PhotoGraphicFileName?.trim() || ''
            )}`}
            alt="Member"
            onError={(e) => (e.currentTarget.src = '/img/100x100.png')}
            style={{ width: 42, height: 42, borderRadius: '999px' }}
          />
        ),
      },
      { field: 'LastName', headerName: 'Last Name', flex: 1, minWidth: 140 },
      { field: 'FirstName', headerName: 'First Name', flex: 1, minWidth: 140 },
      { field: 'ClubMembershipTypeCode', headerName: 'Membership Type', flex: 1, minWidth: 170 },
      { field: 'ClubStatusRuleCode', headerName: 'Club Status Rule', flex: 1, minWidth: 170 },
      {
        field: 'EmailAddress',
        headerName: 'Email',
        flex: 1.4,
        minWidth: 220,
        renderCell: (params) => {
          const email = params.value?.trim();
          return email ? (
            <Tooltip title={`Send email to ${email}`}>
              <Box
                component="a"
                href={`mailto:${email}`}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  color: '#1976d2',
                  textDecoration: 'none',
                  minWidth: 0,
                }}
              >
                <EmailIcon fontSize="small" />
                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                  {email}
                </Typography>
              </Box>
            </Tooltip>
          ) : (
            'N/A'
          );
        },
      },
      {
        field: 'FullAddress',
        headerName: 'Address',
        flex: 2,
        minWidth: 280,
        renderCell: (params) => {
          const address = params.value?.trim();
          const mapsLink = address
            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
            : null;

          return address ? (
            <Tooltip title={`View ${address} on Google Maps`}>
              <Box
                component="a"
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  color: '#1976d2',
                  textDecoration: 'none',
                  minWidth: 0,
                }}
              >
                <LocationOnIcon fontSize="small" />
                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                  {address}
                </Typography>
              </Box>
            </Tooltip>
          ) : (
            'N/A'
          );
        },
      },
    ];
  }, []);

  // ✅ Auto-adjust grid minWidth based on visible columns
  const gridMinWidth = useMemo(() => {
    const minWidthMap = {
      CompanyCode: 140,
      ClubMemberCode: 170,
      PhotoGraphicFileName: 110,
      LastName: 140,
      FirstName: 140,
      ClubMembershipTypeCode: 170,
      ClubStatusRuleCode: 170,
      EmailAddress: 220,
      FullAddress: 280,
    };

    const visibleFields = Object.entries(columnVisibilityModel)
      .filter(([, visible]) => visible)
      .map(([field]) => field);

    const total = visibleFields.reduce((sum, field) => sum + (minWidthMap[field] || 140), 0);
    return Math.max(520, total + 40);
  }, [columnVisibilityModel]);

  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / limit));

  // --------- Cards view UI ---------
  const MemberCard = ({ m }) => {
    const email = m.EmailAddress?.trim();
    const address = m.FullAddress?.trim();
    const mapsLink = address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
      : null;

    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          '&:hover': { boxShadow: '0 6px 18px rgba(0,0,0,0.06)' },
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <img
              loading="lazy"
              src={`${API_BASE_URL}/images/${encodeURIComponent(m?.PhotoGraphicFileName?.trim() || '')}`}
              alt="Member"
              onError={(e) => (e.currentTarget.src = '/img/100x100.png')}
              style={{ width: 44, height: 44, borderRadius: '999px', flex: '0 0 auto' }}
            />

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontWeight: 900 }} noWrap>
                {m.LastName?.trim() || '—'}, {m.FirstName?.trim() || '—'}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                <Chip size="small" label={m.ClubMemberCode?.trim() || 'No Code'} />
                {m.CompanyCode?.trim() ? (
                  <Chip size="small" variant="outlined" label={m.CompanyCode.trim()} />
                ) : null}
                {m.ClubMembershipTypeCode?.trim() ? (
                  <Chip size="small" variant="outlined" label={m.ClubMembershipTypeCode.trim()} />
                ) : null}
              </Box>
            </Box>

            <Button
              component={RouterLink}
              to={`/home/clubmembers/${m.ClubMemberCode}`}
              variant="outlined"
              size="small"
              sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2, whiteSpace: 'nowrap' }}
            >
              Open
            </Button>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 800, width: 90, color: 'text.secondary' }}>
                Status
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                {m.ClubStatusRuleCode?.trim() || '—'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 800, width: 90, color: 'text.secondary' }}>
                Email
              </Typography>

              {email ? (
                <Box
                  component="a"
                  href={`mailto:${email}`}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    color: '#1976d2',
                    textDecoration: 'none',
                    minWidth: 0,
                  }}
                >
                  <EmailIcon fontSize="small" />
                  <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                    {email}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2">N/A</Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 800, width: 90, color: 'text.secondary' }}>
                Address
              </Typography>

              {address ? (
                <Box
                  component="a"
                  href={mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    color: '#1976d2',
                    textDecoration: 'none',
                    minWidth: 0,
                  }}
                >
                  <LocationOnIcon fontSize="small" />
                  <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                    {address}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2">N/A</Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3, minWidth: 0 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 800 }}>
        Club Members
      </Typography>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25, flexWrap: 'wrap', minWidth: 0 }}>
        <TextField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, member code, address..."
          size="small"
          fullWidth
          sx={{
            maxWidth: 760,
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
          <Button
            variant="contained"
            onClick={refreshCurrent}
            disabled={loading}
            startIcon={<RefreshIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 2.25,
              boxShadow: 'none',
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
                boxShadow: '0 4px 12px rgba(25,118,210,0.25)',
              },
              '&:active': { transform: 'translateY(1px)' },
              '&:disabled': { backgroundColor: '#90caf9', color: '#fff' },
              whiteSpace: 'nowrap',
            }}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>

          <Button
            variant="outlined"
            onClick={clearAllMembersCache}
            disabled={loading}
            startIcon={<ClearAllIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 2,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: '#d32f2f',
                color: '#d32f2f',
                backgroundColor: 'rgba(211,47,47,0.04)',
              },
              '&:active': { transform: 'translateY(1px)' },
              whiteSpace: 'nowrap',
            }}
          >
            Clear Cache
          </Button>

          {/* Card/Table toggle */}
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            sx={{
              ml: { xs: 0, md: 0.5 },
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: 2,
                px: 1.5,
              },
            }}
          >
            <ToggleButton value="table">
              <ViewListIcon fontSize="small" style={{ marginRight: 6 }} />
              Table
            </ToggleButton>
            <ToggleButton value="cards">
              <ViewModuleIcon fontSize="small" style={{ marginRight: 6 }} />
              Cards
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Showing <strong>{displayedRows.length}</strong> of <strong>{totalRecords || members.length}</strong> members
      </Typography>

      {/* Container */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',

          // ✅ critical: allow children to shrink inside flex layouts
          minWidth: 0,

          // ✅ do NOT clip horizontal scrolling
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {viewMode === 'cards' ? (
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1.25,
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                  }}
                >
                  {cardsRows.map((m) => (
                    <MemberCard key={m.ClubMemberCode} m={m} />
                  ))}
                </Box>

                {displayedRows.length > cardsRows.length && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => setCardVisibleCount((c) => Math.min(displayedRows.length, c + (isXs ? 12 : 24)))}
                      sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2, px: 2.5 }}
                    >
                      Load more
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <>
                {isXs && (
                  <Typography variant="caption" sx={{ px: 2, pt: 1, color: 'text.secondary', display: 'block' }}>
                    Swipe left/right to see more columns →
                  </Typography>
                )}

                {/* ✅ Bullet-proof horizontal scroll wrapper */}
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    display: 'block',
                  }}
                >
                  <Box
                    sx={{
                      minWidth: gridMinWidth,
                      width: 'max-content',
                      maxWidth: '100%',
                    }}
                  >
                    <DataGrid
                      rows={displayedRows}
                      columns={columns}
                      getRowId={(row) => row.ClubMemberCode}
                      autoHeight
                      disableRowSelectionOnClick
                      hideFooter
                      rowHeight={58}
                      columnVisibilityModel={columnVisibilityModel}
                      sx={{
                        border: 'none',
                        minWidth: gridMinWidth,
                        width: '100%',

                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: 'rgba(0,0,0,0.03)',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 800 },
                        '& .MuiDataGrid-cell': {
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                        '& .MuiDataGrid-columnSeparator': { display: 'none' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                        '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': { px: 2 },
                      }}
                    />
                  </Box>
                </Box>
              </>
            )}

            {/* Bottom pagination */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.25,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fff',
                minWidth: 0,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Page <strong>{currentPage}</strong> of <strong>{Math.max(1, Math.ceil((totalRecords || 0) / limit))}</strong>
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  disabled={loading || currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 2 }}
                >
                  Prev
                </Button>
                <Button
                  variant="outlined"
                  disabled={loading || currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 2 }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default MembersGridPage;
